import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import CASE_STUDIES from '../data/caseStudies';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const listVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const SectionEyebrow = ({ label }) => (
  <div className="svc-eyebrow">
    <span className="svc-eyebrow-line" />
    <span className="svc-eyebrow-text">{label}</span>
    <span className="svc-eyebrow-line" />
  </div>
);

const CaseStudyMedia = ({ study, clip, preview = false }) => {
  const video = clip?.src || study.video;
  const image = clip?.poster || study.image;
  const alt = clip?.alt || study.title;

  if (video) {
    return (
      <video
        src={video}
        poster={image}
        muted
        playsInline
        loop
        autoPlay={preview}
        preload="metadata"
      />
    );
  }

  return <img src={image} alt={alt} />;
};

const CaseStudiesPage = () => {
  const gridRef = useRef(null);
  const gridInView = useInView(gridRef, { once: true, margin: '-80px' });
  const [featured, ...rest] = CASE_STUDIES;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://monkstack.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Case Studies',
        item: 'https://monkstack.com/case-studies',
      },
    ],
  };

  const collectionPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Case Studies - monkStack',
    description:
      'Explore Monkstack engineering case studies across video analytics, PropTech, manufacturing ERP, e-commerce, industrial digitization, enterprise operations, and AI infrastructure.',
    url: 'https://monkstack.com/case-studies',
  };

  return (
    <div className="App cs-page">
      <SEO
        title="Case Studies - AI & Engineering Solutions"
        description="Explore Monkstack engineering case studies including AI video analytics for warehouses, AI ERP for manufacturing, multi-tenant construction takeoff SaaS, e-commerce agents, industrial digitization, enterprise operations, and AI infrastructure."
        url="https://monkstack.com/case-studies"
        image="https://monkstack.com/img/case-studies/video-analytics-poster.jpg"
        structuredData={[breadcrumbSchema, collectionPageSchema]}
      />
      <Navbar />

      <section className="cs-hero">
        <div className="container">
          <motion.div className="cs-hero-inner" initial="hidden" animate="visible" variants={listVariants}>
            <motion.div variants={fadeUp}>
              <SectionEyebrow label="Case Studies" />
            </motion.div>
            <motion.h1 className="svc-heading" variants={fadeUp}>
              Work that ships in production
            </motion.h1>
            <motion.p className="svc-subtext" variants={fadeUp}>
              Engineering case studies from platforms we design, build, and operate — spanning video analytics, PropTech, manufacturing, commerce, industrial digitization, and enterprise AI.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="cs-listing" ref={gridRef}>
        <div className="container">
          <motion.div
            initial="hidden"
            animate={gridInView ? 'visible' : 'hidden'}
            variants={listVariants}
          >
            <motion.div variants={itemVariants}>
              <Link
                to={`/case-studies/${featured.slug}`}
                className={`cs-featured${featured.videos?.length ? ' cs-featured--videos' : ''}`}
              >
                <div className={featured.videos?.length ? 'cs-featured-media-pair' : 'cs-featured-media'}>
                  {featured.videos?.length ? (
                    featured.videos.map((clip) => (
                      <div key={clip.src} className="cs-featured-media">
                        <CaseStudyMedia study={featured} clip={clip} preview />
                      </div>
                    ))
                  ) : (
                    <CaseStudyMedia study={featured} preview />
                  )}
                </div>
                <div className="cs-featured-body">
                  <span className="cs-tag">{featured.industry}</span>
                  <h2 className="cs-featured-title">{featured.title}</h2>
                  <p className="cs-featured-tagline">{featured.tagline}</p>
                  <span className="cs-read-link">Read case study →</span>
                </div>
              </Link>
            </motion.div>

            <div className="cs-grid">
              {rest.map((study) => (
                <motion.div key={study.slug} variants={itemVariants}>
                  <Link to={`/case-studies/${study.slug}`} className="cs-card">
                    <div className="cs-card-media">
                      <CaseStudyMedia study={study} preview />
                    </div>
                    <div className="cs-card-body">
                      <span className="cs-tag">{study.industry}</span>
                      <h3 className="cs-card-title">{study.shortTitle}</h3>
                      <p className="cs-card-tagline">{study.tagline}</p>
                      <span className="cs-read-link">Read case study →</span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudiesPage;
